import type { AuthTokens, PublicUser } from '@messe/shared'

export const API_URL: string =
  (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://127.0.0.1:3000'

/** An RFC 9457 problem document, as every API error arrives. */
export interface ProblemDetails {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  errors?: { path: string; message: string }[]
  requestId?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
    this.status = problem.status
    this.problem = problem
  }

  /** Field-level messages keyed by field name, for react-hook-form. */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {}
    for (const error of this.problem.errors ?? []) {
      result[error.path] ??= error.message
    }
    return result
  }
}

/**
 * The access token lives here and nowhere else.
 *
 * Deliberately a module variable rather than localStorage or a cookie readable
 * by script: it does not survive a reload, which is exactly what makes a stolen
 * XSS payload unable to lift a long-lived credential. On reload the refresh
 * cookie — httpOnly, invisible to JavaScript — silently restores the session.
 */
let accessToken: string | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/**
 * Shared promise for an in-flight refresh.
 *
 * Several queries failing with 401 at once must trigger exactly one refresh.
 * Without this they would each rotate the token, and every rotation but the
 * last would present an already-spent token — which the server correctly reads
 * as theft and responds to by ending the whole session.
 */
let refreshInFlight: Promise<boolean> | null = null

async function parseProblem(response: Response): Promise<ProblemDetails> {
  try {
    const body = (await response.json()) as ProblemDetails
    if (typeof body.status === 'number' && typeof body.title === 'string') {
      return body
    }
    return { type: 'about:blank', title: response.statusText, status: response.status }
  } catch {
    return { type: 'about:blank', title: response.statusText, status: response.status }
  }
}

/**
 * Once the refresh cookie has been rejected, stop asking.
 *
 * Without this an anonymous visitor's failed refresh is retried by every query
 * that follows, and each retry fails the same way — a loop that hammers the
 * server and never resolves. Cleared on a successful sign-in.
 */
let refreshKnownToFail = false

export function resetRefreshState(): void {
  refreshKnownToFail = false
}

/** Exchanges the refresh cookie for a new access token. */
export function refreshSession(): Promise<boolean> {
  if (refreshKnownToFail) return Promise.resolve(false)
  if (refreshInFlight) return refreshInFlight

  const attempt = (async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Sends the httpOnly refresh cookie.
        credentials: 'include',
        body: JSON.stringify({}),
      })

      if (!response.ok) {
        setAccessToken(null)
        refreshKnownToFail = true
        return false
      }

      const tokens = (await response.json()) as AuthTokens
      setAccessToken(tokens.accessToken)
      return true
    } catch {
      setAccessToken(null)
      refreshKnownToFail = true
      return false
    }
  })()

  // Assigned before the first await can settle, and cleared only after the
  // promise has resolved. Writing this as `refreshInFlight ??= (...)()` looks
  // equivalent but is not: a synchronous path through the function would clear
  // the variable before the outer assignment sets it, and the lock would then
  // never be released.
  refreshInFlight = attempt
  void attempt.finally(() => {
    refreshInFlight = null
  })

  return attempt
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Set internally to stop a refreshed request from refreshing again. */
  retryOnUnauthorized?: boolean
}

/**
 * Performs an API call, refreshing the access token once if it has expired.
 *
 * Returns undefined for 204 responses, which have no body.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retryOnUnauthorized = true } = options

  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (accessToken) headers.authorization = `Bearer ${accessToken}`

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await refreshSession()
    if (refreshed) {
      return apiRequest<T>(path, { ...options, retryOnUnauthorized: false })
    }
  }

  if (!response.ok) {
    throw new ApiError(await parseProblem(response))
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

// --- Endpoints ---------------------------------------------------------------

export async function login(email: string, password: string): Promise<AuthTokens> {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  })

  if (!response.ok) {
    throw new ApiError(await parseProblem(response))
  }

  const tokens = (await response.json()) as AuthTokens
  setAccessToken(tokens.accessToken)
  // A fresh cookie is in place; refreshing is worth attempting again.
  resetRefreshState()
  return tokens
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    })
  } finally {
    // Even if the call fails, this browser is done with the session.
    setAccessToken(null)
  }
}

export function fetchMe(): Promise<PublicUser> {
  return apiRequest<PublicUser>('/auth/me')
}
